<template>
<div :class="{ 'spreadsheet-scroller': isScroller, 'spreadsheet-overflowing': hasHorizontalOverflow }">
  <div class="pb-3 d-flex align-center">
    <b>{{ title }}</b>
    <v-spacer></v-spacer>
    <v-btn
      v-if="hasHorizontalOverflow && !isFullscreen"
      icon
      small
      :title="$t('components.ekrm.Spreadsheet.fullscreen')"
      @click="toggleFullscreen"
    >
      <v-icon>mdi-fullscreen</v-icon>
    </v-btn>
  </div>
  <div :id="id" :ref="id"></div>
  <v-btn
    v-if="isFullscreen"
    icon
    class="spreadsheet-fullscreen-exit"
    :title="$t('components.ekrm.Spreadsheet.exitFullscreen')"
    @click="toggleFullscreen"
  >
    <v-icon>mdi-fullscreen-exit</v-icon>
  </v-btn>
</div>
</template>

<script>
import numbro from 'numbro'
import jspreadsheet from 'jspreadsheet-ce'
import 'jspreadsheet-ce/dist/jspreadsheet.css'
import { copyTextToClipboard } from '@/utils/clipboard.js'
import 'jsuites/dist/jsuites.css'

export default {
  data () {
    return {
      data: this.value,
      jspreadsheetInstance: null,
      isFullscreen: false,
      hasHorizontalOverflow: false
    }
  },
  methods: {
    toggleFullscreen () {
      this.isFullscreen = !this.isFullscreen
      if (this.jspreadsheetInstance) {
        this.jspreadsheetInstance.fullscreen(this.isFullscreen)
      }
    },
    checkOverflow () {
      // Compare the grid's natural width against the space actually available
      // (the component root is a block element that fills the available width).
      // This works whether the table width is a fixed px value or '100%', and
      // reads 0 while the spreadsheet is hidden (e.g. an inactive stepper step).
      const table = this.$el.querySelector('.jexcel')
      const available = this.$el.clientWidth
      const overflowing = !!table && available > 0 && table.offsetWidth > available + 1
      if (overflowing === this.hasHorizontalOverflow) return
      this.hasHorizontalOverflow = overflowing
      // Tells the parent that the grid itself now owns the horizontal scroll,
      // so any scroll wrapper it put around us can stop being a second
      // scroller (see ExternalSeriesTable.vue). Only emitted once the state
      // actually changes, and only ever true while .jexcel_content is
      // guaranteed to be showing its own bar.
      this.$emit('overflow', overflowing)
    }
  },
  computed: {
    // jspreadsheet only turns .jexcel_content into a scroll container when it
    // is given a tableWidth (index.js: it sets overflow-x/width on the content
    // only inside `if (obj.options.tableWidth)`). Without one the grid is meant
    // to size to its content, so the clamping styles below must not apply.
    isScroller () {
      return !!this.width
    },
    // Includes HTML tags used for display only
    seriesFormattedNames () {
      return this.data.map(arr => {
        return arr[0]
      })
    },
    id: function () {
      return (Math.random() + 1).toString(36).substring(7)
    },
    jExcelOptions: function () {
      return {
        data: this.data,
        contextMenu: function () {
          return false
        },
        columns: this.columns,
        style: this.customStyles,
        onbeforepaste: function (instance, data, x, y) {
          // Ignore pasting in first column
          if (x === 0 || x === '0') {
            return ' '
          }
          // Ignore first row if it corresponds to the header
          let lines = data.split('\n')
          if (lines.length > 0) {
            const firstLineElements = lines[0].split('\t')
            // Check if the second element is not a number (column title)
            if (firstLineElements.length > 1 && !/^-?\d+(\.\d+)?$/.test(firstLineElements[1])) {
              // Remove first line, because it's a header
              lines.shift()
            }
          }
          // Ignore first column if it corresponds to the series names, and it's being pasted on x > 0
          const dataFirstLineElements = lines[0].split('\t')
          if (x > 0 && dataFirstLineElements.length > 1 && !/^-?\d+(\.\d+)?$/.test(dataFirstLineElements[0])) {
            lines = lines.map(line => line.split('\t').slice(1).join('\t'))
          }
          return lines.join('\n')
          // Disabled this because of problems when pasting for some users
          // Manage bug that ignores an empty value in the last cell (because it's a \n) when pasting more than one column from a spreadhseet
          /* if (data && data[data.length - 1].charCodeAt(0) === 10) {
            data = data.slice(0, data.length) + '\t' // Replace that last \n with a \t (this \t will be replaced by '' on onbeforechange)
          } */
          // return data
        },
        onbeforechange: (el, cell, x, y, value) => {
          // Disabled this because of problems when pasting for some users
          // Manage weird bug that replaces an empty value in the last cell (because it's a \t) with 0 when pasting exactly one column from a spreadhseet. Also replace the \t when insertes in onbeforepaste
          /* if (value.charCodeAt(0) === 0) {
            value = ''
          } */
          // Only allow numbers, periods, and hyphens (and formulas)
          if ((value || value === 0) && value !== '' && value[0] !== '=') {
            let sanitized = value.replace(/[^0-9.-]/gim, '')
            if (sanitized.length > 1) {
              // Filter hyphens in the middle or end of strings loger than 1
              const firstChar = sanitized[0]
              const restOfString = sanitized.slice(1).replaceAll('-', '')
              sanitized = firstChar.concat(restOfString)
            }
            if (isNaN(sanitized)) {
              // Filter strings consisting of a single hyphen
              if ('defaultEmptyValue' in this.columns[x]) {
                return this.columns[x].defaultEmptyValue
              } else {
                return ''
              }
            } else {
              if (this.minMaxByRow && this.minMaxByRow[y]) {
                if (this.minMaxByRow[y][0] > sanitized) {
                  sanitized = this.minMaxByRow[y][0]
                } else if (this.minMaxByRow[y][1] < sanitized) {
                  sanitized = this.minMaxByRow[y][1]
                }
              }
              // The row index arrives from jspreadsheet as a STRING, so an allowNegativesRows given
              // as numbers matched nothing and every cell in it had its sign stripped — silently,
              // the value coming back positive with no error to read. Both are compared as strings
              // so a caller may write the rows either way.
              const negativeRows = this.columns[x].allowNegativesRows
              const negativesHere = Array.isArray(negativeRows) &&
                negativeRows.some(row => String(row) === String(y))
              if (this.columns[x].allowNegatives || negativesHere) {
                return this.columns[x].disableDecimals ? Math.floor(sanitized) : sanitized
              } else {
                return this.columns[x].disableDecimals ? Math.floor(Math.abs(sanitized)) : Math.abs(sanitized)
              }
            }
          } else {
            // Replace '' (user deletes cell) with defaultValue when this prop is provided
            if (value === '' && this.defaultValues) {
              return this.defaultValues[y][x]
            } else if (value === '' && 'defaultEmptyValue' in this.columns[x]) {
              // Replace '' (user deletes cell) with defaultEmptyValue when this prop is provided
              return this.columns[x].defaultEmptyValue
            } else {
              return value
            }
          }
        },
        onchange: (el, cellDOM, x, y, value, oldValue) => {
          this.$emit('input', this.data)
        },
        oncopy: (_, rowsName) => {
          console.log('Copying')
          console.log(rowsName)
          const data = rowsName.map(item => {
            // Ignore Series names column if present
            const includesSeriesColumn = this.seriesFormattedNames.includes(item.split(/\t/, 1)[0])
            const regex = new RegExp(/<[^>]+>/g)
            // Remove HTML tags from all copied values
            const noHTMLItem = item.replace(regex, '')
            // Split the string at every tab
            let parts = noHTMLItem.split(/\t/)
            // Remove " and , characters from numeric cells (so "1,000" becomes 1000)
            // Also remove $ and % symbols
            if (parts.length > 0) {
              // Create regex to match characters to remove
              let regex2 = new RegExp('[' + ['"', ',', '$', '%', ' '] + ']', 'g')
              // Remove the characters from each part
              for (let index = 0; index < parts.length; index++) {
                // Ignore Series names column if present
                if (!includesSeriesColumn || index !== 0) {
                  parts[index] = parts[index].replace(regex2, '')
                }
              }
            }
            // Recombine the parts into a single string with tabs
            return parts.join('\t')
          })
          // Overwrite what jspreadsheet just put on the clipboard with the
          // cleaned values. Its own copy() writes the visible labels through the
          // hidden textarea and document.execCommand, so copyTextToClipboard is
          // handed that same textarea: it is already selected and holds the
          // keyboard focus, and reusing it keeps the copy from moving the focus
          // out of the grid. If both paths fail the clipboard simply keeps
          // jspreadsheet's own text, i.e. the values as displayed.
          const instance = this.jspreadsheetInstance
          copyTextToClipboard(data.join('\n'), instance ? instance.textarea : null)
            .then(copied => {
              if (!copied) {
                console.warn('Could not copy the formatted values to the clipboard')
              }
            })
        },
        columnSorting: false,
        allowInsertRow: false,
        allowInsertColumn: false,
        tableOverflow: true,
        includeHeadersOnCopy: true,
        copyCompatibility: true,
        tableWidth: this.width,
        tableHeight: this.height,
        nestedHeaders: this.nestedHeaders,
        freezeColumns: '1',
        mergeCells: this.mergeCells,
        updateTable: (instance, cell, col, row, val, label, cellName) => {
          // Number formating
          if (val && (!isNaN(val) || (isNaN(val) && !isNaN(cell.innerText)))) {
            // Get text
            let txt = cell.innerText
            if (isNaN(val)) {
              // Assign text as val when val is a formula
              val = cell.innerText
            }
            // Format text
            txt = numbro(val).format({
              thousandSeparated: true,
              optionalMantissa: false,
              trimMantissa: true
            })
            if (this.columns[col].customFormat === '$') {
              cell.innerHTML = '$ ' + txt
            } else if (this.columns[col].customFormat === '%') {
              cell.innerHTML = txt + ' %'
            } else {
              cell.innerHTML = txt
            }
          }
          // Row editable disabling
          if (this.disabledRows.includes(row) && this.disabledRowsStartColumn <= col) {
            cell.classList.add('readonly')
            cell.style.backgroundColor = '#fffaa3'
          }
          // Column editable disabling
          if (this.disabledColumns.includes(col)) {
            cell.classList.add('readonly')
            cell.style.backgroundColor = '#fffaa3'
          }
        }
      }
    }
  },
  mounted: function () {
    const primaryColor = this.$vuetify.theme.themes.light.primary
    const jExcelObj = jspreadsheet(this.$refs[this.id], this.jExcelOptions)
    Object.assign(this, { jExcelObj })
    this.jspreadsheetInstance = document.getElementById(this.id).jspreadsheet
    let array = this.jspreadsheetInstance.headers
    for (let index = 0; index < array.length; index++) {
      array[index].style.color = primaryColor
    }
    document.querySelectorAll('.jexcel_nested').forEach(function (item) { item.style.color = primaryColor })
    // document.getElementById(this.id).jspreadsheet.hideIndex() // Cannot use because of bug in freezeColumn used together with this
    // Hack to allow deletion of selections
    document.addEventListener('keydown', function (e) {
      if (jspreadsheet.current) {
        if (jspreadsheet.current.edition) {
          if (e.key === 'Backspace' && jspreadsheet.current.highlighted.length > 1) { // 8 = Backspace key code
            jspreadsheet.current.setValue(jspreadsheet.current.highlighted, '')
            e.stopPropagation()
          }
        }
      }
    })
    /* document.addEventListener('copy', function (e) {
      if (e.target.id === 'jexcel_textarea') {
        // Prevent default copy behaviour
        e.preventDefault()
        console.log(e)
      }
    }) */
    // Show the fullscreen button only when the table overflows horizontally.
    // A ResizeObserver re-checks whenever the grid is laid out or resized,
    // which also covers the case where the spreadsheet is initially hidden
    // (e.g. inside an inactive v-stepper-content) and becomes visible later.
    this.onEscape = (e) => {
      if (e.key === 'Escape' && this.isFullscreen) {
        this.toggleFullscreen()
      }
    }
    document.addEventListener('keydown', this.onEscape)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => { this.checkOverflow() })
      this.resizeObserver.observe(this.$el)
    }
    this.$nextTick(() => { this.checkOverflow() })
  },
  beforeDestroy: function () {
    document.removeEventListener('keydown', this.onEscape)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  watch: {
    value: {
      immediate: true,
      handler () {
        if (this.value && this.value !== this.data) {
          this.data = this.value
          try {
            this.jspreadsheetInstance.setData(this.data)
            // This is needed to make mergeCells reactive
            this.jspreadsheetInstance.destroyMerged()
            Object.entries(this.mergeCells).forEach(entry => {
              this.jspreadsheetInstance.setMerge(entry[0], entry[1][0], entry[1][1])
            })
          } catch (error) {}
          this.$nextTick(() => { this.checkOverflow() })
        }
      }
    },
    customStyles: {
      immediate: true,
      handler () {
        if (this.customStyles) {
          try {
            this.jspreadsheetInstance.setStyle(this.customStyles)
          } catch (error) {}
        }
      }
    }
  },
  props: {
    value: {
      type: Array
    },
    columns: {
      type: Array,
      required: true
    },
    nestedHeaders: {
      type: Array,
      required: false,
      default: function () {
        return []
      }
    },
    width: {
      type: String,
      required: false
    },
    height: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false
    },
    mergeCells: {
      type: Object,
      required: false
    },
    disabledRows: {
      type: Array,
      required: false,
      default: () => {
        return []
      }
    },
    disabledRowsStartColumn: {
      type: Number,
      required: false,
      default: () => {
        return 0
      }
    },
    disabledColumns: {
      type: Array,
      required: false,
      default: () => {
        return []
      }
    },
    minMaxByRow: {
      type: Array,
      required: false
    },
    customStyles: {
      // Defines custom CSS styles for specific cells
      type: Object,
      required: false
    },
    defaultValues: {
      // These values, when provided, are used to fill deleted cells
      type: Array,
      required: false
    }
  }
}
</script>

<style>
/* Lift the jspreadsheet fullscreen overlay above Vuetify dialogs
   (default library z-index is 21, which sits behind v-dialog) */
.jexcel_container.fullscreen {
  z-index: 9999 !important;
}
/* Floating exit control rendered on top of the fullscreen grid */
.spreadsheet-fullscreen-exit {
  position: fixed;
  top: 8px;
  right: 8px;
  z-index: 10000;
}
/* The whole point of the rules below is a single invariant: when the grid is
   wider than the space it has, exactly ONE element paints a horizontal
   scrollbar, and it is .jexcel_content. Two nested scrollers look identical on
   macOS — its overlay scrollbars take no layout space and are not painted at
   rest, so only the styled one below is ever visible — but on Linux and Windows
   every scroller that has something to scroll paints a permanent bar, so a
   second one shows up as a duplicate. The invariant is what keeps the three
   platforms looking the same; see ExternalSeriesTable.vue for the other half. */

/* jspreadsheet's container is display:inline-block, so a table sized with
   width="100%" expands the content to its full natural width and never scrolls
   internally (the overflow lands on an ancestor instead). Constrain the
   container to the available width so .jexcel_content is the real horizontal
   scroller. Applied whenever a tableWidth was given rather than only while
   overflowing: which element scrolls must not depend on when checkOverflow()
   happens to run (it is driven by a ResizeObserver, and these grids mount
   inside dialogs and steppers that resize after mount). A fixed-px tableWidth
   is clamped the same way, so it scrolls internally instead of breaking out of
   its container. */
.spreadsheet-scroller .jexcel_container {
  display: block !important;
  max-width: 100% !important;
}
.spreadsheet-scroller .jexcel_content {
  max-width: 100% !important;
  box-sizing: border-box;
}

/* While overflowing, keep that one scrollbar always visible (classic, not
   overlay/hover-only) in both normal and fullscreen views, so the control looks
   and behaves the same on every platform. jspreadsheet sets the standard
   scrollbar-width/scrollbar-color on .jexcel_content, which makes Chrome ignore
   ::-webkit-scrollbar rules; resetting them to their default (auto) re-enables
   the webkit pseudo-elements so -webkit-appearance: none can force an always-on
   classic bar — including on macOS, where styling the pseudo-element is what
   opts the scrollbar out of overlay mode. */
.spreadsheet-overflowing .jexcel_content {
  overflow-x: scroll !important;
  scrollbar-width: auto !important;
  scrollbar-color: auto !important;
}
.spreadsheet-overflowing .jexcel_content::-webkit-scrollbar {
  height: 12px;
  -webkit-appearance: none;
}
.spreadsheet-overflowing .jexcel_content::-webkit-scrollbar-track {
  background: #eeeeee;
}
.spreadsheet-overflowing .jexcel_content::-webkit-scrollbar-thumb {
  background: #999999;
  border-radius: 6px;
  border: 2px solid #eeeeee;
}
/* Firefox has no ::-webkit-scrollbar, and scrollbar-color: auto leaves it in
   overlay mode on macOS, so the bar there faded out while every other browser
   kept it. Naming the colours is what makes Gecko draw a non-overlay, always
   visible bar; it is scoped to Firefox (the same @supports probe jspreadsheet's
   own stylesheet uses) so Chrome keeps honouring the webkit rules above. */
@supports (-moz-appearance: none) {
  .spreadsheet-overflowing .jexcel_content {
    scrollbar-width: auto !important;
    scrollbar-color: #999999 #eeeeee !important;
  }
}
</style>
